/* eslint-disable @typescript-eslint/strict-boolean-expressions */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
import React, { useState, useEffect } from 'react';
import { Spin, Table, Select, Button, DatePicker, Card, Row, Col } from 'antd';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { type AppDispatch } from '../../../redux/store';
import { getOrgTransactionsAction } from '../store/expenseActions';
import { getOrgTransactionsSummaryAction } from '../../Overview/store/overviewActions';
import { type TransactionResponse } from '../store/expenseTypes';
import fmt from 'indian-number-format';

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

export default function OrganizationTransactionsIndex(): JSX.Element {
  const generatePDF = (transactionsArray: any) => {
    const groupedByType = transactionsArray.reduce((acc: any, item: any) => {
      if (!acc[item.type]) {
        acc[item.type] = [];
      }
      acc[item.type].push(item);
      return acc;
    }, {});

    // Define the desired order of types
    const typeOrder = [
      'Deposit',
      'Expense',
      'Investment',
      'Loan',
      'LoanPayment',
      'Pledge',
      'PledgePayment',
      'BranchTransfer',
    ];

    // Create a new jsPDF document
    // eslint-disable-next-line new-cap
    const doc = new jsPDF({ orientation: 'landscape' });
    doc.setFontSize(18);

    const branchLabelForHeader = branchOptions.filter(
      (x) => x.value === branch
    )[0]?.label;

    // Balance summary tables at the start of the PDF
    doc.text('Balance Summary', 14, 20);
    doc.setFontSize(10);
    doc.text(
      `Branch: ${branchLabelForHeader ?? ''}   |   ${startDate} to ${endDate}`,
      14,
      27
    );
    doc.setFontSize(18);

    const summarySections: Array<{ title: string; values: number[] }> = [
      {
        title: 'Total Balance',
        values: [
          balanceSummary.openingBalance,
          balanceSummary.periodIn,
          balanceSummary.periodOut,
          balanceSummary.closingBalance,
        ],
      },
      {
        title: 'Online Balance',
        values: [
          balanceSummary.openingBalanceOnline,
          balanceSummary.periodInOnline,
          balanceSummary.periodOutOnline,
          balanceSummary.closingBalanceOnline,
        ],
      },
      {
        title: 'Offline Balance',
        values: [
          balanceSummary.openingBalanceOffline,
          balanceSummary.periodInOffline,
          balanceSummary.periodOutOffline,
          balanceSummary.closingBalanceOffline,
        ],
      },
    ];

    let summaryY = 38;

    summarySections.forEach((section) => {
      doc.setFontSize(12);
      doc.text(section.title, 14, summaryY);

      autoTable(doc, {
        startY: summaryY + 4,
        theme: 'grid',
        head: [
          ['Opening Balance', 'Amount In', 'Amount Out', 'Closing Balance'],
        ],
        body: [section.values.map((value) => fmt.format(Number(value).toFixed(0)))],
        styles: {
          cellPadding: 3,
          fontSize: 9,
          valign: 'middle',
          halign: 'center',
        },
      });

      summaryY = (doc as any).lastAutoTable.finalY + 12;
    });

    doc.setFontSize(12);
    doc.text('Stock Balance', 14, summaryY);
    autoTable(doc, {
      startY: summaryY + 4,
      theme: 'grid',
      head: [['Total Grams', 'Loan Count', 'Stock Value']],
      body: [
        [
          Number(stockBalance.grams).toFixed(2),
          stockBalance.loanCount,
          fmt.format(Number(stockBalance.value).toFixed(0)),
        ],
      ],
      styles: {
        cellPadding: 3,
        fontSize: 9,
        valign: 'middle',
        halign: 'center',
      },
    });

    doc.setFontSize(18);

    // Start Y coordinate for the first table
    const currentY = 20;

    // Iterate through the typeOrder to maintain the specific order of tables
    typeOrder.forEach((type) => {
      if (groupedByType[type]) {
        // Each transaction type gets its own page, after the summary page
        doc.addPage();

        doc.text(type, 14, currentY);

        const startY = currentY + 10; // Adjust starting Y position for table after title

        const additionalHeaderTitle =
          type === 'Loan' || type === 'LoanPayment'
            ? 'loanNumber'
            : type === 'Pledge' || type === 'PledgePayment'
            ? 'pledgeNumber'
            : '';

        // Define headers for the table
        const headers = [
          [
            'S.No',
            'Description',
            'Amount',
            'Online',
            'Offline',
            'Transaction Date',
            'Created Date',
            'Created Time',
            additionalHeaderTitle,
          ],
        ];

        // Remove the last header if no additional title is required
        if (!additionalHeaderTitle) {
          headers[0].pop();
        }

        // Map body data for the table
        const body = groupedByType[type].map((item: any, idx: number) => [
          idx + 1,
          item.description,
          item.amount,
          item.onlineAmount ?? 0,
          item.offlineAmount ?? 0,
          dayjs(item.transactionDate as string).format('DD-MM-YYYY'),
          dayjs(item.createdDate as string).format('DD-MM-YYYY'),
          dayjs(item.createdDate as string).format('hh:mm A'),
          additionalHeaderTitle
            ? item.transactionMappingData[additionalHeaderTitle]
            : '',
        ]);

        autoTable(doc, {
          startY,
          theme: 'grid',
          head: headers,
          body,
          columnStyles: {
            [headers[0].length - 1]: { cellWidth: 24 },
          },
          styles: {
            cellPadding: 3,
            fontSize: 6,
            valign: 'middle',
            halign: 'center',
          },
        });
      }
    });

    // Save the PDF
    doc.save(
      `Transactions-${branchLabelForHeader}-${startDate}-${endDate}.pdf`
    );
  };

  const { RangePicker } = DatePicker;

  const dispatch = useDispatch<AppDispatch>();

  const { orgTransactions }: any = useSelector<any>((state) => state?.expense);
  const { orgTransactionsSummary }: any = useSelector<any>((state) => state?.overview);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Balance summary from API
  const balanceSummary = {
    openingBalance: orgTransactionsSummary?.openingBalance ?? 0,
    periodIn: orgTransactionsSummary?.periodIn ?? 0,
    periodOut: orgTransactionsSummary?.periodOut ?? 0,
    closingBalance: orgTransactionsSummary?.closingBalance ?? 0,
    openingBalanceOnline: orgTransactionsSummary?.openingBalanceOnline ?? 0,
    periodInOnline: orgTransactionsSummary?.periodInOnline ?? 0,
    periodOutOnline: orgTransactionsSummary?.periodOutOnline ?? 0,
    closingBalanceOnline: orgTransactionsSummary?.closingBalanceOnline ?? 0,
    openingBalanceOffline: orgTransactionsSummary?.openingBalanceOffline ?? 0,
    periodInOffline: orgTransactionsSummary?.periodInOffline ?? 0,
    periodOutOffline: orgTransactionsSummary?.periodOutOffline ?? 0,
    closingBalanceOffline: orgTransactionsSummary?.closingBalanceOffline ?? 0,
  };

  // Gold still in the branch at the end of the selected period
  const stockBalance = {
    grams: orgTransactionsSummary?.stockGrams ?? 0,
    loanCount: orgTransactionsSummary?.stockLoanCount ?? 0,
    value: orgTransactionsSummary?.stockValue ?? 0,
  };

  const [branchOptions, setBranchOptions] = useState<
    Array<{
      label: string;
      value: string;
    }>
  >([]);

  const [branch, setBranch] = useState<any>('');

  useEffect(() => {
    const branches = localStorage.getItem('branches');
    if (branches != null) {
      const parsedBranches = JSON.parse(branches);
      const branchOptionTemp: Array<{ label: string; value: string }> =
        parsedBranches.map((branch: { code: string; id: string }) => ({
          label: branch.code,
          value: branch.id,
        }));
      setBranchOptions(branchOptionTemp);
      setBranch(parsedBranches[0].id);
    }
  }, []);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    if (branch) {
      void getTransactions();
    }
  }, [branch]);

  const getTransactions = async (): Promise<void> => {
    setIsLoading(true);
    await Promise.all([
      getOrgTransactionsAction(dispatch, {
        branch,
        startDate,
        endDate,
      }),
      getOrgTransactionsSummaryAction(dispatch, {
        branch,
        startDate,
        endDate,
      }),
    ]);
    setIsLoading(false);
  };

  const columns = [
    {
      title: 'S.No',
      dataIndex: 'sno',
      render: (_: any, record: TransactionResponse, index: number) => index + 1,
      width: 50,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (_: any, record: TransactionResponse, index: number) => (
        <p>
          {record.description}{' '}
          {record.additionalDescription
            ? `-${record.additionalDescription}`
            : ''}
        </p>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (_: any, record: TransactionResponse, index: number) => (
        <p style={{ color: record.status === 'In' ? 'green' : 'red' }}>
          {fmt.format(record.amount)}
        </p>
      ),
    },
    {
      title: 'Online',
      dataIndex: 'onlineAmount',
      key: 'onlineAmount',
      render: (_: any, record: TransactionResponse, index: number) => (
        <p style={{ color: record.status === 'In' ? 'green' : 'red' }}>
          {fmt.format(record.onlineAmount ?? 0)}
        </p>
      ),
    },
    {
      title: 'Offline',
      dataIndex: 'offlineAmount',
      key: 'offlineAmount',
      render: (_: any, record: TransactionResponse, index: number) => (
        <p style={{ color: record.status === 'In' ? 'green' : 'red' }}>
          {fmt.format(record.offlineAmount ?? 0)}
        </p>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (_: any, record: TransactionResponse, index: number) => (
        <p>{record.type}</p>
      ),
    },
    {
      title: 'Transaction Date',
      dataIndex: 'date',
      key: 'date',
      render: (_: any, record: TransactionResponse, index: number) => (
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
        <p>{dayjs(record.transactionDate as string).format('DD-MM-YYYY')}</p>
      ),
    },
    {
      title: 'Created Date',
      dataIndex: 'date',
      key: 'date',
      render: (_: any, record: TransactionResponse, index: number) => (
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
        <p>{dayjs(record.createdDate as string).format('DD-MM-YYYY')}</p>
      ),
    },
    {
      title: 'Created Time',
      dataIndex: 'date',
      key: 'date',
      render: (_: any, record: TransactionResponse, index: number) => (
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
        <p>{dayjs(record.createdDate as string).format('hh:mm A')}</p>
      ),
    },
  ];

  const dateFormat = 'YYYY-MM-DD';

  const [startDate, setStartDate] = useState<string>(
    dayjs().format('YYYY-MM-DD')
  );
  const [endDate, setEndDate] = useState<string>(dayjs().format('YYYY-MM-DD'));

  const onChangeDate = (values: any, formatString: [string, string]) => {
    setStartDate(formatString[0]);
    setEndDate(formatString[1]);
  };

  return (
    <div className="org_transactions__page">
      <Spin spinning={isLoading} fullscreen />

      <div style={{ display: 'flex', flexDirection: 'row', gap: 32 }}>
        <Select
          value={branch}
          onChange={(value: string): void => {
            setBranch(value);
          }}
          style={{ width: 311, height: 56 }}
          placeholder="Select branch"
          options={branchOptions}
          className="expense__input"
        />
        <RangePicker
          className="rangepicker"
          onChange={onChangeDate}
          defaultValue={[dayjs(startDate), dayjs(endDate)]}
          format={dateFormat}
        />
        <Button onClick={getTransactions} className="search__button">
          <span>Search</span>
        </Button>
        {Boolean(orgTransactions.length) && (
          <Button
            onClick={() => {
              generatePDF(orgTransactions);
            }}
            className="search__button"
          >
            <span>Download PDF</span>
          </Button>
        )}
      </div>
      {Boolean(orgTransactions.length) && (
        <div style={{ width: '100%' }}>
          {/* Balance Summary Section */}
          <div style={{ marginBottom: 24, marginTop: 16 }}>
            <Row gutter={[16, 16]}>
              {/* Total Row */}
              <Col span={24}>
                <Card title="Total Balance" size="small">
                  <Row gutter={16}>
                    <Col xs={12} sm={6}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 12, color: '#666' }}>Opening Balance</div>
                        <div style={{ fontSize: 16, fontWeight: 'bold' }}>
                          {fmt.format(balanceSummary.openingBalance.toFixed(0))}
                        </div>
                      </div>
                    </Col>
                    <Col xs={12} sm={6}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 12, color: '#666' }}>Amount In</div>
                        <div style={{ fontSize: 16, fontWeight: 'bold', color: 'green' }}>
                          {fmt.format(balanceSummary.periodIn.toFixed(0))}
                        </div>
                      </div>
                    </Col>
                    <Col xs={12} sm={6}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 12, color: '#666' }}>Amount Out</div>
                        <div style={{ fontSize: 16, fontWeight: 'bold', color: 'red' }}>
                          {fmt.format(balanceSummary.periodOut.toFixed(0))}
                        </div>
                      </div>
                    </Col>
                    <Col xs={12} sm={6}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 12, color: '#666' }}>Closing Balance</div>
                        <div style={{ fontSize: 16, fontWeight: 'bold' }}>
                          {fmt.format(balanceSummary.closingBalance.toFixed(0))}
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Card>
              </Col>
              {/* Online Row */}
              <Col span={24}>
                <Card title="Online Balance" size="small">
                  <Row gutter={16}>
                    <Col xs={12} sm={6}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 12, color: '#666' }}>Opening Balance</div>
                        <div style={{ fontSize: 16, fontWeight: 'bold' }}>
                          {fmt.format(balanceSummary.openingBalanceOnline.toFixed(0))}
                        </div>
                      </div>
                    </Col>
                    <Col xs={12} sm={6}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 12, color: '#666' }}>Amount In</div>
                        <div style={{ fontSize: 16, fontWeight: 'bold', color: 'green' }}>
                          {fmt.format(balanceSummary.periodInOnline.toFixed(0))}
                        </div>
                      </div>
                    </Col>
                    <Col xs={12} sm={6}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 12, color: '#666' }}>Amount Out</div>
                        <div style={{ fontSize: 16, fontWeight: 'bold', color: 'red' }}>
                          {fmt.format(balanceSummary.periodOutOnline.toFixed(0))}
                        </div>
                      </div>
                    </Col>
                    <Col xs={12} sm={6}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 12, color: '#666' }}>Closing Balance</div>
                        <div style={{ fontSize: 16, fontWeight: 'bold' }}>
                          {fmt.format(balanceSummary.closingBalanceOnline.toFixed(0))}
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Card>
              </Col>
              {/* Offline Row */}
              <Col span={24}>
                <Card title="Offline Balance" size="small">
                  <Row gutter={16}>
                    <Col xs={12} sm={6}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 12, color: '#666' }}>Opening Balance</div>
                        <div style={{ fontSize: 16, fontWeight: 'bold' }}>
                          {fmt.format(balanceSummary.openingBalanceOffline.toFixed(0))}
                        </div>
                      </div>
                    </Col>
                    <Col xs={12} sm={6}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 12, color: '#666' }}>Amount In</div>
                        <div style={{ fontSize: 16, fontWeight: 'bold', color: 'green' }}>
                          {fmt.format(balanceSummary.periodInOffline.toFixed(0))}
                        </div>
                      </div>
                    </Col>
                    <Col xs={12} sm={6}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 12, color: '#666' }}>Amount Out</div>
                        <div style={{ fontSize: 16, fontWeight: 'bold', color: 'red' }}>
                          {fmt.format(balanceSummary.periodOutOffline.toFixed(0))}
                        </div>
                      </div>
                    </Col>
                    <Col xs={12} sm={6}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 12, color: '#666' }}>Closing Balance</div>
                        <div style={{ fontSize: 16, fontWeight: 'bold' }}>
                          {fmt.format(balanceSummary.closingBalanceOffline.toFixed(0))}
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Card>
              </Col>
              {/* Stock Row */}
              <Col span={24}>
                <Card title="Stock Balance" size="small">
                  <Row gutter={16}>
                    <Col xs={8} sm={8}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 12, color: '#666' }}>Total Grams</div>
                        <div style={{ fontSize: 16, fontWeight: 'bold' }}>
                          {Number(stockBalance.grams).toFixed(2)}
                        </div>
                      </div>
                    </Col>
                    <Col xs={8} sm={8}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 12, color: '#666' }}>Loan Count</div>
                        <div style={{ fontSize: 16, fontWeight: 'bold' }}>
                          {stockBalance.loanCount}
                        </div>
                      </div>
                    </Col>
                    <Col xs={8} sm={8}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 12, color: '#666' }}>Stock Value</div>
                        <div style={{ fontSize: 16, fontWeight: 'bold' }}>
                          {fmt.format(Number(stockBalance.value).toFixed(0))}
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Card>
              </Col>
            </Row>
          </div>
          <Table
            dataSource={orgTransactions}
            columns={columns}
            rowKey={(record: TransactionResponse) => record._id}
          />
        </div>
      )}
      {orgTransactions.length === 0 && (
        <div style={{ width: '100%' }}>
          <p>No transactions available for the branch</p>
        </div>
      )}
    </div>
  );
}
